;(() => {
  const instances = new Map()

  class LogoListMarquee {
    constructor(root) {
      this.root = root
      this.track = root.querySelector('.logo-list__track')
      this.group = root.querySelector('.logo-list__group')
      this.speed = Number(root.dataset.autoSpeed) || 18
      this.scrollResponse = Number(root.dataset.scrollResponse) || 0
      this.pauseOnHover = root.dataset.pauseOnHover === 'true'
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.position = 0
      this.groupWidth = 0
      this.lastFrame = 0
      this.lastScrollY = window.scrollY
      this.visible = true
      this.hovered = false
      this.detailOpen = false
      this.activeBlockId = ''
      this.frame = 0
      this.pointerFrame = 0
      this.closeTimer = 0
      this.scrollIdleTimer = 0
      this.pageScrolling = false

      this.onScroll = this.onScroll.bind(this)
      this.onFrame = this.onFrame.bind(this)
      this.onVisibilityChange = this.onVisibilityChange.bind(this)
      this.onClick = this.onClick.bind(this)
      this.onKeydown = this.onKeydown.bind(this)
      this.onPointerMove = this.onPointerMove.bind(this)
      this.onBlockSelect = this.onBlockSelect.bind(this)
      this.onBlockDeselect = this.onBlockDeselect.bind(this)
      this.onMouseEnter = () => { this.hovered = true }
      this.onMouseLeave = () => { this.hovered = false }

      this.root.addEventListener('click', this.onClick)
      this.root.addEventListener('keydown', this.onKeydown)
      this.root.addEventListener('pointermove', this.onPointerMove, { passive: true })
      document.addEventListener('shopify:block:select', this.onBlockSelect)
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect)

      this.revealObserver = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) {
          this.root.classList.add('is-visible')
          this.revealObserver?.disconnect()
        }
      }, { threshold: 0.18 })
      this.revealObserver.observe(this.root)

      if (!this.track || !this.group || root.dataset.layout !== 'marquee') {
        this.root.classList.add('is-visible')
        return
      }

      this.resizeObserver = new ResizeObserver(() => this.measure())
      this.resizeObserver.observe(this.group)

      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.visible = entries[0]?.isIntersecting ?? true
        if (this.visible) {
          this.lastFrame = performance.now()
          this.startFrameLoop()
        } else {
          this.stopFrameLoop()
        }
      }, { rootMargin: '160px 0px' })
      this.intersectionObserver.observe(this.root)

      window.addEventListener('scroll', this.onScroll, { passive: true })
      document.addEventListener('visibilitychange', this.onVisibilityChange)

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.onMouseEnter)
        this.root.addEventListener('mouseleave', this.onMouseLeave)
      }

      this.measure()
      this.startFrameLoop()
    }

    startFrameLoop() {
      if (this.frame || !this.visible || document.hidden || this.pageScrolling) return
      this.frame = requestAnimationFrame(this.onFrame)
    }

    stopFrameLoop() {
      if (!this.frame) return
      cancelAnimationFrame(this.frame)
      this.frame = 0
    }

    measure() {
      this.groupWidth = this.group.getBoundingClientRect().width
      if (this.groupWidth > 0) this.normalize()
    }

    normalize() {
      if (!this.groupWidth) return
      while (this.position <= -this.groupWidth) this.position += this.groupWidth
      while (this.position > 0) this.position -= this.groupWidth
      this.track.style.transform = `translate3d(${this.position.toFixed(2)}px, 0, 0)`
    }

    onScroll() {
      const currentScrollY = window.scrollY
      const delta = Math.max(-140, Math.min(140, currentScrollY - this.lastScrollY))
      this.lastScrollY = currentScrollY

      if (!this.visible || this.reducedMotion.matches || !this.groupWidth) return

      this.pageScrolling = true
      this.stopFrameLoop()
      if (this.scrollIdleTimer) window.clearTimeout(this.scrollIdleTimer)
      this.scrollIdleTimer = window.setTimeout(() => {
        this.scrollIdleTimer = 0
        this.pageScrolling = false
        this.lastFrame = performance.now()
        this.startFrameLoop()
      }, 160)

      this.position -= delta * this.scrollResponse
      this.normalize()
    }

    onVisibilityChange() {
      this.lastFrame = performance.now()
      if (document.hidden) this.stopFrameLoop()
      else this.startFrameLoop()
    }

    onClick(event) {
      const trigger = event.target.closest('[data-logo-trigger]')
      if (trigger && this.root.contains(trigger)) {
        this.toggleDetail(trigger.dataset.logoTrigger, trigger)
        return
      }

      const close = event.target.closest('[data-logo-close]')
      if (close && this.root.contains(close)) this.closeDetail({ restoreFocus: true })
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.detailOpen) {
        event.preventDefault()
        this.closeDetail({ restoreFocus: true })
        return
      }

      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return
      const current = event.target.closest('[data-logo-trigger]:not([data-logo-duplicate])')
      if (!current || !this.root.contains(current)) return

      const triggers = [...this.root.querySelectorAll('[data-logo-trigger]:not([data-logo-duplicate])')]
      const index = triggers.indexOf(current)
      if (index < 0) return

      event.preventDefault()
      const direction = event.key === 'ArrowRight' ? 1 : -1
      const next = triggers[(index + direction + triggers.length) % triggers.length]
      next.focus()
    }

    onPointerMove(event) {
      if (this.pointerFrame || event.pointerType === 'touch') return
      this.pointerFrame = requestAnimationFrame(() => {
        const rect = this.root.getBoundingClientRect()
        const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100))
        const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100))
        this.root.style.setProperty('--logo-list-pointer-x', `${x.toFixed(1)}%`)
        this.root.style.setProperty('--logo-list-pointer-y', `${y.toFixed(1)}%`)
        this.pointerFrame = 0
      })
    }

    onBlockSelect(event) {
      if (!this.root.contains(event.target)) return
      const blockId = event.detail?.blockId
      if (blockId) this.openDetail(blockId)
    }

    onBlockDeselect(event) {
      if (this.root.contains(event.target)) this.closeDetail()
    }

    toggleDetail(blockId, trigger) {
      if (this.detailOpen && this.activeBlockId === blockId) {
        this.closeDetail({ restoreFocus: true })
        return
      }
      this.openDetail(blockId, trigger)
    }

    openDetail(blockId, trigger) {
      const detail = this.root.querySelector(`[data-logo-detail="${CSS.escape(blockId)}"]`)
      if (!detail) return
      window.clearTimeout(this.closeTimer)

      this.root.querySelectorAll('[data-logo-detail]').forEach((panel) => {
        panel.hidden = panel !== detail
      })
      this.root.querySelectorAll('[data-logo-item]').forEach((item) => {
        item.classList.toggle('is-active', item.dataset.logoItem === blockId)
      })
      this.root.querySelectorAll('[data-logo-trigger]:not([data-logo-duplicate])').forEach((button) => {
        button.setAttribute('aria-expanded', String(button.dataset.logoTrigger === blockId))
      })

      this.activeBlockId = blockId
      this.detailOpen = true
      this.activeTrigger = trigger?.matches('[data-logo-duplicate]')
        ? this.root.querySelector(`[data-logo-trigger="${CSS.escape(blockId)}"]:not([data-logo-duplicate])`)
        : trigger || this.root.querySelector(`[data-logo-trigger="${CSS.escape(blockId)}"]:not([data-logo-duplicate])`)
      this.root.classList.add('has-active-logo', 'is-detail-open')
    }

    closeDetail({ restoreFocus = false } = {}) {
      if (!this.detailOpen) return
      this.root.classList.remove('has-active-logo', 'is-detail-open')
      this.root.querySelectorAll('[data-logo-item]').forEach((item) => item.classList.remove('is-active'))
      this.root.querySelectorAll('[data-logo-trigger]:not([data-logo-duplicate])').forEach((button) => {
        button.setAttribute('aria-expanded', 'false')
      })

      const focusTarget = this.activeTrigger
      this.closeTimer = window.setTimeout(() => {
        this.root.querySelectorAll('[data-logo-detail]').forEach((panel) => { panel.hidden = true })
        this.closeTimer = 0
      }, this.reducedMotion.matches ? 0 : 520)

      this.detailOpen = false
      this.activeBlockId = ''
      this.activeTrigger = null
      if (restoreFocus) focusTarget?.focus({ preventScroll: true })
    }

    onFrame(timestamp) {
      this.frame = 0
      if (!this.visible || document.hidden || this.pageScrolling) return
      if (!this.lastFrame) this.lastFrame = timestamp
      const elapsed = Math.min((timestamp - this.lastFrame) / 1000, 0.05)
      this.lastFrame = timestamp

      if (
        this.visible &&
        !document.hidden &&
        !this.reducedMotion.matches &&
        !this.hovered &&
        !this.detailOpen &&
        this.groupWidth
      ) {
        this.position -= this.speed * elapsed
        this.normalize()
      }

      this.startFrameLoop()
    }

    destroy() {
      cancelAnimationFrame(this.frame)
      this.resizeObserver?.disconnect()
      this.intersectionObserver?.disconnect()
      this.revealObserver?.disconnect()
      window.removeEventListener('scroll', this.onScroll)
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      document.removeEventListener('shopify:block:select', this.onBlockSelect)
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect)
      this.root.removeEventListener('mouseenter', this.onMouseEnter)
      this.root.removeEventListener('mouseleave', this.onMouseLeave)
      this.root.removeEventListener('click', this.onClick)
      this.root.removeEventListener('keydown', this.onKeydown)
      this.root.removeEventListener('pointermove', this.onPointerMove)
      cancelAnimationFrame(this.pointerFrame)
      window.clearTimeout(this.closeTimer)
      window.clearTimeout(this.scrollIdleTimer)
      this.track?.style.removeProperty('transform')
      this.root.style.removeProperty('--logo-list-pointer-x')
      this.root.style.removeProperty('--logo-list-pointer-y')
    }
  }

  const init = (scope = document) => {
    const roots = scope.matches?.('[data-logo-list]')
      ? [scope]
      : scope.querySelectorAll?.('[data-logo-list]') || []

    roots.forEach((root) => {
      instances.get(root)?.destroy()
      instances.set(root, new LogoListMarquee(root))
    })
  }

  const unload = (scope) => {
    const roots = scope.matches?.('[data-logo-list]')
      ? [scope]
      : scope.querySelectorAll?.('[data-logo-list]') || []

    roots.forEach((root) => {
      instances.get(root)?.destroy()
      instances.delete(root)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true })
  } else {
    init()
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target))
  document.addEventListener('shopify:section:unload', (event) => unload(event.target))
})()
